import { ArrowRight, TrendingUp, TrendingDown, Minus, FileBarChart2, Shield } from "lucide-react";

/**
 * V229 — Benchmark Sample Preview (/benchmark/sample)
 *
 * The "see-before-you-buy" page. Shows a redacted but realistic sample of
 * the PDF report using the exact 4-company set Zenith used in their FB ad
 * (karcher, zahradnedepo, mountfield, kajo — all public SK domains). This
 * is intentional: anyone who saw the Zenith ad lands here and instantly
 * recognises the same data presented 100× cheaper and self-serve.
 */

const SAMPLE_ROWS = [
  { domain: "karcher.com/sk", revenue: "24 709 320 €", profit: "193 412 €", margin: "0,78 %", trend_rev: "down", trend_profit: "down" },
  { domain: "zahradnedepo.sk", revenue: "104 098 €",    profit: "2 095 €",   margin: "2,01 %", trend_rev: "down", trend_profit: "up" },
  { domain: "mountfield.sk",   revenue: "49 509 835 €", profit: "-2 232 766 €", margin: "-4,51 %", trend_rev: "down", trend_profit: "down" },
  { domain: "kajo.sk",         revenue: "1 860 654 €",  profit: "183 022 €", margin: "9,84 %", trend_rev: "down", trend_profit: "up" },
];

function TrendIcon({ dir }) {
  if (dir === "up")   return <TrendingUp className="w-3.5 h-3.5 text-emerald-500 inline" />;
  if (dir === "down") return <TrendingDown className="w-3.5 h-3.5 text-rose-500 inline" />;
  return <Minus className="w-3.5 h-3.5 text-gray-400 inline" />;
}

export default function BenchmarkSamplePage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="benchmark-sample-page">
      <header className="px-6 py-6 max-w-5xl mx-auto flex items-center justify-between">
        <a href="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
        </a>
        <a
          href="/benchmark"
          data-testid="benchmark-sample-buy-btn"
          className="text-xs sm:text-sm bg-[#a855f7] hover:bg-[#9333ea] text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
        >
          Získať svoj report za €14 <ArrowRight className="w-4 h-4" />
        </a>
      </header>

      <section className="px-6 pt-6 pb-16 max-w-4xl mx-auto">
        <span className="inline-block px-3 py-1 bg-white/5 text-gray-400 text-xs font-bold tracking-widest uppercase mb-4 font-['JetBrains_Mono']">
          UKÁŽKA REPORTU
        </span>
        <h1 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-4">
          Takto vyzerá PDF, ktorý dostanete.
        </h1>
        <p className="text-base text-gray-400 mb-10 max-w-2xl">
          Vzorka s verejnými SK e-shop doménami. Váš report bude mať vaše vlastné
          konkurenčné domény a identický rozsah dát.
        </p>

        {/* Paper-like preview card */}
        <div className="bg-white text-gray-900 rounded-2xl shadow-2xl overflow-hidden"
             data-testid="benchmark-sample-pdf-preview">
          <div className="px-6 sm:px-10 py-8 border-b border-gray-200">
            <p className="text-xs text-gray-500 font-bold tracking-widest font-['JetBrains_Mono'] mb-2">
              05 FINANČNÁ POZÍCIA
            </p>
            <div className="w-16 h-1 bg-[#0a2a5e] mb-1" />
          </div>

          <div className="px-6 sm:px-10 py-8">
            <h2 className="text-xl font-bold text-[#0a2a5e] mb-6 flex items-center gap-2">
              <FileBarChart2 className="w-5 h-5" />
              Porovnanie 4 domén · obchodný rok 2024
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#f3f7ff] text-[#0a2a5e]">
                    <th className="text-left px-3 py-2.5 font-bold">DOMÉNA</th>
                    <th className="text-right px-3 py-2.5 font-bold">OBRAT</th>
                    <th className="text-right px-3 py-2.5 font-bold">ČISTÝ ZISK</th>
                    <th className="text-right px-3 py-2.5 font-bold">ČISTÁ MARŽA</th>
                    <th className="text-center px-3 py-2.5 font-bold">TREND OBRATU</th>
                    <th className="text-center px-3 py-2.5 font-bold">TREND ZISKU</th>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE_ROWS.map((r, i) => (
                    <tr key={r.domain} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                        data-testid={`sample-row-${i}`}>
                      <td className="px-3 py-2.5 font-mono text-gray-900">{r.domain}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{r.revenue}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{r.profit}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.margin}</td>
                      <td className="px-3 py-2.5 text-center">
                        <TrendIcon dir={r.trend_rev} /> <span className={r.trend_rev === "down" ? "text-rose-500" : "text-emerald-600"}>{r.trend_rev === "down" ? "klesá" : "rastie"}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <TrendIcon dir={r.trend_profit} /> <span className={r.trend_profit === "down" ? "text-rose-500" : "text-emerald-600"}>{r.trend_profit === "down" ? "klesá" : "rastie"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-gray-500 mt-5 leading-relaxed">
              Trendy ukazujú medziročnú zmenu oproti predchádzajúcemu účtovnému obdobiu
              (rastie / klesá / bez zmeny). Pomlčka znamená, že dáta pre danú doménu
              neboli dostupné. Zdroj: ORSF.sk + registeruz.sk (CC-BY 4.0 / CC0).
            </p>
          </div>
        </div>

        {/* AI Visibility bonus preview — the unique TRP-only data */}
        <div className="mt-10 bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
          <p className="text-xs font-bold tracking-widest uppercase text-[#FFE600] mb-3 font-['JetBrains_Mono']">
            BONUS SEKCIA — EXKLUZÍVNE V TOPRANKPULSE
          </p>
          <h2 className="text-xl font-bold mb-4">AI Visibility Score</h2>
          <p className="text-sm text-gray-400 mb-5 max-w-2xl">
            Pri každej firme z tabuľky dostanete aj naše AI visibility skóre —
            koľkokrát ich cituje ChatGPT, Perplexity, Gemini, Claude a Copilot pri typických
            dotazoch z ich odvetvia. <strong className="text-white">Tieto dáta nikde inde nedostanete.</strong>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            {[
              ["karcher.com/sk", "72 / 100"],
              ["zahradnedepo.sk", "41 / 100"],
              ["mountfield.sk", "68 / 100"],
              ["kajo.sk", "29 / 100"],
            ].map(([dom, score]) => (
              <div key={dom} className="bg-black/30 border border-white/5 rounded-lg p-3">
                <p className="text-[11px] text-gray-500 font-mono mb-1">{dom}</p>
                <p className="text-2xl font-black text-white tabular-nums">{score}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA band */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#a855f7]/10 border border-[#a855f7]/30 rounded-2xl p-6">
          <div>
            <p className="text-sm text-gray-400 mb-1">Pripravený na svoj report?</p>
            <p className="text-xl font-black">Early-bird €14 (prvých 100 reportov)</p>
          </div>
          <a
            href="/benchmark"
            data-testid="benchmark-sample-cta-btn"
            className="w-full sm:w-auto bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-6 py-4 rounded-lg flex items-center justify-center gap-2 text-base transition-all"
          >
            Objednať report <ArrowRight className="w-5 h-5" />
          </a>
        </div>

        <p className="flex items-center gap-2 justify-center text-xs text-gray-500 mt-6">
          <Shield className="w-3.5 h-3.5" />
          Dáta neopúšťajú EÚ · GDPR-compliant · 14-dní garancia vrátenia peňazí
        </p>
      </section>
    </div>
  );
}
